import { useCallback, useEffect, useState } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import ArticlePreviewCard from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import ArtworkCard from '~/components/atoms/ArtworkCard/ArtworkCard'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { GridProps } from '~/components/atoms/Grid'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import Loader from '~/components/atoms/Loader/Loader'
import ProfileImage from '~/components/atoms/ProfileImage/ProfileImage'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import { useSearch } from '~/services/query'

import Text, { TextSizes } from '../Text/Text'

import styles from './SearchInfiniteLoader.module.css'
import RecordTypes = indexes.RecordTypes
import classNames from 'classnames'

import ArticlesFilter from '~/components/blocks/ArticlesFilter/ArticlesFilter'
import Filters from '~/components/blocks/Filters/Filters'
import {
  getTitles,
  typeMapper,
} from '~/components/blocks/SearchDialogInner/resultTypeMapper'
import { useSettingsContext } from '~/contexts/settingsContext'
import useSearchAnalytics from '~/hooks/useSearchAnalytics'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { toURL } from '~/utils/urlResolver'

import Link from '../Link/Link'
import PortfolioTableArtwork from '../PortfolioTableArtwork/PortfolioTableArtwork'

export interface SearchInifiniteLoaderProps {
  pageSize?: number
  recordType: RecordTypes
  searchTerm: string
  columns: GridProps['variant']
  onResultsLoad: (type: string, number: number) => void
  noBottomPadding?: boolean
  searchInsightsEvent?: TSearchAnalystEventProps
  searchInsightsEvents?: {
    artwork: TSearchAnalystEventProps
    artist?: TSearchAnalystEventProps
  }
  typeSelection?: string | null
}

export default function SearchInifiniteLoader({
  pageSize = 12,
  recordType,
  searchTerm,
  columns,
  onResultsLoad,
  noBottomPadding,
  searchInsightsEvent,
  searchInsightsEvents,
  typeSelection,
}: SearchInifiniteLoaderProps) {
  const t = useTranslations('searchInfiniteLoader')
  const tSearchResultTypes = useTranslations('searchResultTypes')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const { handleSearchResultsClicked } = useSearchAnalytics()
  const { allArtists } = useSettingsContext()

  // Initialize default filters
  const [filters, setFilters] = useState({
    artists: Array(0),
    genres: Array(0),
    formats: Array(0),
    prices: Array(0),
    tradingFloor: false as any,
    filter: '',
    artist: '',
  })

  // Parse URL parameters for filters (only for artwork type)
  useEffect(() => {
    if (recordType === RecordTypes.ARTWORK && typeof window === 'object') {
      const urlParams = new URLSearchParams(window?.location.search)

      const genreFilters = urlParams?.get('genres')
        ? JSON.parse(String(urlParams?.get('genres')))
        : []

      const artistFilters = urlParams?.get('artists')
        ? JSON.parse(String(urlParams?.get('artists')))
        : []

      const formatFilters = urlParams?.get('formats')
        ? JSON.parse(String(urlParams?.get('formats')))
        : []

      const priceFilters = urlParams?.get('prices')
        ? JSON.parse(String(urlParams?.get('prices')))
        : []

      const tradingFloor = urlParams?.get('trading-floor')
        ? urlParams?.get('trading-floor')
        : false

      setFilters({
        artists: artistFilters,
        genres: genreFilters,
        formats: formatFilters,
        prices: priceFilters,
        tradingFloor,
        filter: '',
        artist: '',
      })
    }

    if (recordType === RecordTypes.ARTICLES && typeof window === 'object') {
      const urlParams = new URLSearchParams(window?.location.search)
      const articleFilter = urlParams?.get('filter')
      const articleArtist = urlParams?.get('artist')

      setFilters({
        artists: [],
        genres: [],
        formats: [],
        prices: [],
        tradingFloor: false,
        filter: articleFilter as any,
        artist: articleArtist as any,
      })
    }
  }, [recordType, window?.location.search])

  const { data, error, size, setSize, facets, isLoading } = useSearch(
    pageSize,
    recordType,
    searchTerm,
    filters
  )

  let insightsQueryID: string | undefined = undefined
  const processedData: any[] = []

  if (data) {
    data?.forEach(result => {
      insightsQueryID = result.insightsQueryID
      result.hits?.forEach((hit: any) => {
        const _value = JSON.stringify(hit)
        if (
          processedData.findIndex(obj => JSON.stringify(obj) === _value) < 0
        ) {
          processedData.push(hit)
        }
      })
    })
  }

  const total = data?.[0]?.total ?? 0
  const isLoadingInitialData = !data && !error

  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined')

  let title: React.ReactNode | string =
    processedData && processedData.length > 1
      ? recordType === RecordTypes.COLLECTION
        ? `${!isMobile ? `${tSearchResultTypes('artist')} ` : ''}${tSearchResultTypes('collection')}`
        : getTitles(recordType, tSearchResultTypes)
      : typeMapper(recordType, tSearchResultTypes)

  if (data && processedData.length > 0) {
    title = (
      <>
        {title} <span>({total})</span>
      </>
    )
  }

  useEffect(() => {
    if (data && processedData.length > 0) {
      onResultsLoad(`${recordType}`, total)
    } else if (data) {
      onResultsLoad(`${recordType}`, 0)
    }
  }, [data])

  const loadMoreHandler = () => {
    setSize(size + 1)
  }

  const getSectionLink = () => {
    switch (recordType) {
      case RecordTypes.ARTWORK:
        return '/artworks'
      case RecordTypes.ARTIST:
        return '/artists'
      case RecordTypes.COLLECTION:
        return '/artworks'
      case RecordTypes.ARTICLES:
        return '/articles'
      default:
        return ''
    }
  }

  const renderArtworkFilters = () => {
    const { genre, artist, format } = facets || {}

    const hasFilters = genre || artist || format ? true : false

    if (!hasFilters) {
      // return null
    }

    if (isLoading) return <Loader />

    return (
      <Filters
        noMarginleft
        filters={filters}
        genres={facets?.genre}
        artists={facets?.artist as any}
        formats={facets?.format}
        prices={facets?.price}
        viewAll={false}
        searchTerm={''}
        testID={`artworkslist-search`}
        clearSearchTerm={undefined}
        hideTradingFloor={true}
        filterQueries={
          undefined
          // queryId ? [{ term: { artistId: queryId } }] : undefined
        }
        totals={{ total: total || 0, totalTitle: t('artworks') }}
        // searchInsightsEvents={searchInsightsEvents}
        hideFilters={undefined}
      />
    )
  }

  const renderArticleFilters = useCallback(() => {
    if (isLoading) return <Loader />

    const articleArtists = facets?.artist
      ? facets?.artist.map((item: any) => {
          const artist = allArtists?.find(
            (artist: any) => artist.slug === item.slug
          )
          return {
            ...artist,
            _id: item._id,
            name: item.name,
            image: artist?.image,
            slug: item.slug,
            totalArticles: item.totalArticles,
          }
        })
      : []

    return (
      <ArticlesFilter
        categoryList={facets?.category}
        articleArtists={articleArtists}
        consolidateFilters={true}
        hideArtist={false}
        isArtistIndex={false}
        articlesTypeSlug={false}
        theme={'magazine'}
        addSpacerTop={false}
        noMargins
      />
    )
  }, [allArtists, facets?.artist, facets?.category, isLoading])

  const hasFilters = [
    RecordTypes.ARTWORK,
    RecordTypes.ARTICLES,
    RecordTypes.PODCASTS,
    RecordTypes.VIDEOS,
  ].includes(recordType)

  return (
    <>
      {typeSelection && typeSelection === recordType && hasFilters && (
        <div className={styles.filterContainer}>
          <div className={styles.mainFilters}>
            {recordType === RecordTypes.ARTWORK && renderArtworkFilters()}
            {[
              RecordTypes.ARTICLES,
              RecordTypes.PODCASTS,
              RecordTypes.VIDEOS,
            ].includes(recordType) && renderArticleFilters()}
          </div>
          <div className={styles.sortContainer}></div>
        </div>
      )}
      {processedData && processedData.length > 0 && !error && (
        <div className={styles.base} id={recordType}>
          <Fixture
            verticalPadding={true}
            halfPadding={true}
            noPaddingTop={isMobile || hasFilters}
            noPaddingBottom={noBottomPadding}
          >
            {!typeSelection && (
              <div
                className={classNames(styles.header, {
                  [styles.reducedBottom]: [RecordTypes.ARTWORK].includes(
                    recordType
                  ),
                })}
              >
                <Text
                  size={TextSizes.primaryTitleMed}
                  Tag="h4"
                  className={styles.typeTitle}
                >
                  {title}
                </Text>
                <Link href={getSectionLink()}>
                  <Text
                    size={TextSizes.primaryTitleMed}
                    className={styles.viewAllButton}
                  >
                    {t('viewAll')}
                  </Text>
                </Link>
              </div>
            )}
            <Grid>
              <Row>
                {processedData.map((item: any, i: number) => {
                  return (
                    <Col variant={columns} key={`${recordType}-result-${i}`}>
                      {recordType === RecordTypes.ARTWORK &&
                        (!typeSelection ? (
                          <Link
                            href={toURL({
                              slug: item.artworkSlug,
                              artist: item.artistSlug,
                              collection: item.collectionSlug,
                              collectionTaxonomy: item.collectionTaxonomy,
                              _type: 'artwork',
                            })}
                            noPrefetch
                            onClick={() => {
                              handleSearchResultsClicked(searchTerm, {
                                searchClickCategory: RecordTypes.ARTWORK,
                                positionalIndex: i,
                                searchClickArtist: item.artist,
                                searchClickArtwork: item.title,
                              })
                            }}
                            className={styles.artworkCardLink}
                            testAttr="artwork-card-link"
                          >
                            <PortfolioTableArtwork item={item} extendedInfo />
                          </Link>
                        ) : (
                          <div className={styles.artworkCardLarge}>
                            <ArtworkCard
                              {...item}
                              insightsQueryID={insightsQueryID}
                              itemIndex={i + 1}
                              searchInsightsEvents={searchInsightsEvents}
                              handleAnalytics={() => {
                                handleSearchResultsClicked(searchTerm, {
                                  searchClickCategory: RecordTypes.ARTWORK,
                                  positionalIndex: i,
                                  searchClickArtist: item.artist,
                                  searchClickArtwork: item.title,
                                })
                              }}
                            />
                          </div>
                        ))}
                      {recordType === RecordTypes.ARTICLES && (
                        <ArticlePreviewCard
                          {...item}
                          type={RecordTypes.ARTICLES}
                          variant={isMobile ? 'search' : 'medium'}
                          titleTag="h3"
                          insightsQueryID={insightsQueryID}
                          hideSummary={
                            typeSelection === RecordTypes.ARTICLES
                              ? false
                              : !isMobile
                          }
                          itemIndex={i + 1}
                          showReadMore={false}
                          imageType={isMobile ? 'square' : undefined}
                          searchInsightsEvent={searchInsightsEvent}
                          orientation={isMobile ? 'landscape' : 'portrait'}
                          handleAnalytics={() => {
                            handleSearchResultsClicked(searchTerm, {
                              searchClickCategory: RecordTypes.ARTICLES,
                              positionalIndex: i,
                              searchClickArticle: item.title,
                            })
                          }}
                        />
                      )}
                      {[RecordTypes.PODCASTS, RecordTypes.VIDEOS].includes(
                        recordType
                      ) && (
                        <ArticlePreviewCard
                          {...item}
                          type={recordType}
                          variant={isMobile ? 'search' : 'medium'}
                          titleTag="h3"
                          insightsQueryID={insightsQueryID}
                          hideSummary={
                            typeSelection &&
                            [RecordTypes.PODCASTS, RecordTypes.VIDEOS].includes(
                              typeSelection as RecordTypes
                            )
                              ? false
                              : !isMobile
                          }
                          itemIndex={i + 1}
                          showReadMore={false}
                          imageType={isMobile ? 'square' : undefined}
                          searchInsightsEvent={searchInsightsEvent}
                          orientation={isMobile ? 'landscape' : 'portrait'}
                          enforceNoReadMore
                          handleAnalytics={() => {
                            handleSearchResultsClicked(searchTerm, {
                              searchClickCategory: RecordTypes.ARTICLES,
                              positionalIndex: i,
                              searchClickArticle: item.title,
                            })
                          }}
                        />
                      )}
                      {recordType === RecordTypes.ARTIST && (
                        <div>
                          <ProfileImage
                            variant="landscape"
                            {...item}
                            subtitle={undefined}
                            showNumberOfWorks
                            recordType={recordType}
                            insightsQueryID={insightsQueryID}
                            searchInsightsEvent={searchInsightsEvent}
                            fallBackToInitials
                            clickHandler={() => {
                              handleSearchResultsClicked(searchTerm, {
                                searchClickCategory: RecordTypes.ARTIST,
                                positionalIndex: i,
                                searchClickArtist: item.name,
                              })
                            }}
                          />
                        </div>
                      )}
                      {recordType === RecordTypes.COLLECTION && (
                        <div className={styles.profileImageCard}>
                          {item.collectionCount}
                          <ProfileImage
                            {...item}
                            showNumberOfWorks
                            variant="landscape"
                            recordType={recordType}
                            insightsQueryID={insightsQueryID}
                            searchInsightsEvent={searchInsightsEvent}
                            clickHandler={() => {
                              handleSearchResultsClicked(searchTerm, {
                                searchClickCategory: RecordTypes.COLLECTION,
                                positionalIndex: i,
                                searchClickCollection: item.name,
                              })
                            }}
                          />
                        </div>
                      )}

                      {/* <Spacer size={SpacerSizes.lg} /> */}
                    </Col>
                  )
                })}
              </Row>
            </Grid>

            {isLoadingMore && <Loader />}

            {processedData.length < total && !isLoadingMore && (
              <div
                className={classNames(styles.buttonWrap, {
                  [styles.pushBottom]: [
                    RecordTypes.ARTWORK,
                    RecordTypes.COLLECTION,
                  ].includes(recordType),
                })}
              >
                <Button
                  // iconLeft
                  variant={ButtonVariants.secondaryDark}
                  className={styles.showMoreButton}
                  button={{
                    type: ButtonTypes.button,
                    onClick: loadMoreHandler,
                  }}
                >
                  {t('showMore')}
                </Button>
              </div>
            )}
          </Fixture>
        </div>
      )}
    </>
  )
}
